function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
// Browser-compatible EventEmitter implementation
let SimpleEventEmitter = class SimpleEventEmitter {
    on(event, listener) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(listener);
    }
    emit(event, ...args) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            eventListeners.forEach((listener)=>listener(...args));
        }
    }
    removeAllListeners() {
        this.listeners.clear();
    }
    constructor(){
        _define_property(this, "listeners", new Map());
    }
};
export class McpClient extends SimpleEventEmitter {
    // Common methods
    getState() {
        return {
            ...this.state
        };
    }
    getStatus() {
        return this.state.status;
    }
    isConnected() {
        return this.state.status === 'connected';
    }
    updateStatus(status, error) {
        this.state.status = status;
        if (error) {
            this.state.error = error;
            this.state.lastError = new Date();
        }
        const event = {
            type: status === 'connected' ? 'connected' : status === 'disconnected' ? 'disconnected' : 'error',
            serverId: this.config.id,
            timestamp: new Date(),
            data: error ? {
                error
            } : undefined
        };
        this.emit('statusChange', this.state);
        this.emit('event', event);
    }
    async makeRequest(method, params, timeout = 30000) {
        if (!this.isConnected()) {
            throw new Error('Client is not connected');
        }
        const id = this.requestId++;
        const request = {
            jsonrpc: '2.0',
            id,
            method,
            params
        };
        return new Promise((resolve, reject)=>{
            const timeoutHandle = setTimeout(()=>{
                this.pendingRequests.delete(id);
                reject(new Error(`Request timeout: ${method}`));
            }, timeout);
            this.pendingRequests.set(id, {
                resolve,
                reject,
                timeout: timeoutHandle
            });
            this.sendRequest(request).catch((error)=>{
                this.pendingRequests.delete(id);
                clearTimeout(timeoutHandle);
                reject(error);
            });
        });
    }
    handleResponse(response) {
        const pending = this.pendingRequests.get(response.id);
        if (!pending) {
            console.warn('Received response for unknown request:', response.id);
            return;
        }
        this.pendingRequests.delete(response.id);
        clearTimeout(pending.timeout);
        if (response.error) {
            pending.reject(new Error(response.error.message));
        } else {
            pending.resolve(response.result);
        }
    }
    // MCP Protocol Methods
    async initialize() {
        const result = await this.makeRequest('initialize', {
            protocolVersion: '2024-11-05',
            capabilities: {},
            clientInfo: {
                name: 'react-mcp-client',
                version: '1.0.0'
            }
        });
        this.state.capabilities = result.capabilities;
        this.state.serverInfo = result.serverInfo;
        this.state.lastConnected = new Date();
        return result;
    }
    async listTools() {
        const result = await this.makeRequest('tools/list');
        this.state.tools = result.tools;
        return result.tools;
    }
    async callTool(params) {
        const result = await this.makeRequest('tools/call', params);
        return result;
    }
    async executeTool(toolName, args) {
        const execution = {
            id: `exec-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            serverId: this.config.id,
            tool: toolName,
            arguments: args,
            status: 'pending',
            startTime: new Date()
        };
        this.emit('executionStart', execution);
        try {
            execution.status = 'running';
            const result = await this.callTool({
                name: toolName,
                arguments: args
            });
            execution.status = 'completed';
            execution.result = result;
            execution.endTime = new Date();
            execution.duration = execution.endTime.getTime() - execution.startTime.getTime();
        } catch (error) {
            execution.status = 'failed';
            execution.error = error instanceof Error ? error.message : String(error);
            execution.endTime = new Date();
            execution.duration = execution.endTime.getTime() - execution.startTime.getTime();
        }
        this.emit('executionComplete', execution);
        return execution;
    }
    async listResources() {
        const result = await this.makeRequest('resources/list');
        this.state.resources = result.resources;
        return result.resources;
    }
    async listPrompts() {
        const result = await this.makeRequest('prompts/list');
        this.state.prompts = result.prompts;
        return result.prompts;
    }
    // Cleanup
    destroy() {
        // Clear all pending requests
        for (const [id, pending] of this.pendingRequests){
            clearTimeout(pending.timeout);
            pending.reject(new Error('Client destroyed'));
        }
        this.pendingRequests.clear();
        // Remove all listeners
        this.removeAllListeners();
        // Disconnect if connected
        if (this.isConnected()) {
            this.disconnect().catch(console.error);
        }
    }
    constructor(config){
        super(), _define_property(this, "config", void 0), _define_property(this, "state", void 0), _define_property(this, "requestId", 1), _define_property(this, "pendingRequests", new Map());
        this.config = config;
        this.state = {
            config,
            status: 'disconnected'
        };
    }
}
